import { RedirectRequest } from '@azure/msal-browser';

const TENANT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const TENANT_PARAM = 'tenant';
export const LOGIN_HINT_PARAM = 'login_hint';

/** Satisfied by both URLSearchParams and Angular's ParamMap; both return decoded values. */
export interface QueryReader {
  get(name: string): string | null;
}

export type LoginHint = Partial<Pick<RedirectRequest, 'authority' | 'loginHint'>>;

/**
 * A customer's landing page links here with ?tenant=<id>&login_hint=<upn>.
 *
 * Without them this app signs in at 'common', which resolves to the person's home
 * directory: they arrive as a guest of their own tenant, the organization is matched on
 * its home tenant, and a second sign-in is needed to switch to the directory that
 * actually governs their access. Naming the directory up front makes the first token the
 * right one, and the hint stops Entra asking which account when several are signed in.
 *
 * Anything malformed is ignored rather than forwarded: these values steer where
 * credentials are sent, so a half-understood one must not reach MSAL.
 */
export function readLoginHint(query: QueryReader): LoginHint {
  const hint: LoginHint = {};

  const tenant = query.get(TENANT_PARAM)?.trim();
  if (tenant && TENANT_ID_PATTERN.test(tenant)) {
    hint.authority = `https://login.microsoftonline.com/${tenant.toLowerCase()}`;
  }

  const loginHint = query.get(LOGIN_HINT_PARAM)?.trim();
  if (loginHint && loginHint.length <= 256 && !/\s/.test(loginHint)) {
    hint.loginHint = loginHint;
  }

  return hint;
}

/** Whether either parameter is present, so they can be cleared once they have been used. */
export function hasLoginHintParams(query: QueryReader): boolean {
  return query.get(TENANT_PARAM) !== null || query.get(LOGIN_HINT_PARAM) !== null;
}
